import React, { useEffect } from 'react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useToast } from './use-toast';

interface ToastProps {
  id: string;
  title: string;
  description?: string;
  variant?: 'default' | 'success' | 'error' | 'warning';
  onDismiss: (id: string) => void;
}

export const Toast: React.FC<ToastProps> = ({
  id,
  title,
  description,
  variant = 'default',
  onDismiss,
}) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onDismiss(id);
    }, 5000);

    return () => clearTimeout(timer);
  }, [id, onDismiss]);

  return (
    <div
      className={cn(
        'pointer-events-auto relative flex w-full max-w-md rounded-lg border p-4 shadow-lg',
        'transform transition-all duration-300 ease-in-out',
        'animate-in slide-in-from-right-full fade-in',
        {
          'bg-dark-200 border-dark-300 text-white': variant === 'default',
          'bg-green-500/10 border-green-500/20 text-green-500': variant === 'success',
          'bg-red-500/10 border-red-500/20 text-red-500': variant === 'error',
          'bg-yellow-500/10 border-yellow-500/20 text-yellow-500': variant === 'warning',
        }
      )}
    >
      <div className="flex-1">
        <div className="flex justify-between items-start">
          <h3 className="font-medium">{title}</h3>
          <button
            onClick={() => onDismiss(id)}
            className="ml-4 inline-flex h-6 w-6 items-center justify-center rounded-md hover:bg-dark-300 focus:outline-none"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        {description && <div className="mt-1 text-sm opacity-90">{description}</div>}
      </div>
    </div>
  );
};

export const Toaster: React.FC = () => {
  const { toasts, dismiss } = useToast();

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 w-full max-w-md">
      {toasts.map((toast) => (
        <Toast
          key={toast.id}
          id={toast.id}
          title={toast.title}
          description={toast.description}
          variant={toast.variant}
          onDismiss={dismiss}
        />
      ))}
    </div>
  );
};